import { useTranslation } from "react-i18next";
import React, { useEffect, useState } from "react";
import { message, Spin } from "antd";
import AbsenceFilters from "./AbsenceFilters";
import AbsenceTable from "./AbsenceTable";
import useTableFilters from "../../../../components/Common/TableFilter/useTableFilters";
import { useAbsences } from "../hooks/useAbsences";
import { updateAbsence } from "../../../../services/absenceService";
import { Absence } from "../../../../types/absenceTypes";
import { formatToSQLDate } from "../../../../utils/dateUtils";

interface AbsenceListProps {
  role: "pedagogy-director" | "student" | "guardian";
  studentId?: string;
}

const AbsenceList: React.FC<AbsenceListProps> = ({ role, studentId }) => {
  const { absences: originalAbsences, loading, refresh } = useAbsences({ role, studentId });
  const [filteredAbsences, setFilteredAbsences] = useState<Absence[]>([]);
  const { filters, setFilters, resetFilters } = useTableFilters({
    studentName: "",
    class: "",
    date: null,
    status: "",
  });
  const { t } = useTranslation();

  // Apply table‐level filters
  useEffect(() => {
    setFilteredAbsences(
      originalAbsences.filter((absence) => {
        const studentFullName = `${absence.student?.user?.firstName ?? ""} ${absence.student?.user?.lastName ?? ""}`.toLowerCase();
        const className = (absence.student?.class?.name ?? "").toLowerCase();
        const absenceDate = formatToSQLDate(absence.dateTime);
        const status = absence.status ?? "";

        return (
          (!filters.studentName || studentFullName.includes((filters.studentName as string).toLowerCase())) &&
          (!filters.class || className.includes((filters.class as string).toLowerCase())) &&
          (!filters.date || absenceDate === filters.date) &&
          (!filters.status || status === (filters.status as string))
        );
      })
    );
  }, [filters, originalAbsences]);

  const handleUpdateStatus = async (
    absenceId: string,
    status: "excused" | "unexcused"
  ) => {
    try {
      await updateAbsence(absenceId, { status });
      refresh();
      if (status === "excused") {
        message.success(t("absence_marked_as_excused"));
      } else {
        message.success(t("absence_marked_as_unexcused"));
      }
    } catch {
      message.error(t("failed_to_update_absence"));
    }
  };

  return (
    <>
      <div className="d-flex mb-3 justify-content-end gap-2">
        {role === "pedagogy-director" && (
          <AbsenceFilters
            filters={filters}
            onApply={setFilters}
            onReset={resetFilters}
          />
        )}
      </div>

      <div>
        {loading ? (
          <Spin size="large" className="d-flex justify-content-center my-5" />
        ) : (
          <AbsenceTable
            absences={filteredAbsences as any}
            role={role}
            onUpdateStatus={handleUpdateStatus}
            onRefresh={refresh}
          />
        )}
      </div>
    </>

  );
};

export default AbsenceList;
